import { useState } from 'react';

import { useRouteAndQueryParams } from '@april9au/stack9-react';
import {
  S9ComponentConfigCategory,
  S9ComponentConfigRelease,
} from '@april9au/stack9-sdk';
import type { S9ComponentConfig } from '@april9au/stack9-sdk';
import { S9CustomEntityFormPage, S9PageSection } from '@april9au/stack9-ui';
import * as ui from '@april9au/stack9-ui';
import { Descriptions, Tag } from 'antd';
import dayjs from 'dayjs';

import { BookingDrawer } from '../components/BookingDrawer/BookingDrawer';
import { AppRoutes } from '../constants/appRoutes';
import { DBBooking } from '../models/stack9/Booking';

type BookingDetail = DBBooking & {
  event?: {
    id: number;
    event_name: string;
    event_type?: string;
    booking_type?: string;
  };
  user?: {
    id: number;
    full_name: string;
    email?: string;
  };
  slot?: {
    id: number;
    start_dt: string;
    end_dt: string;
  };
  department?: {
    id: number;
    name: string;
  };
  status?: string;
  workflow?: string;
};

export const config: S9ComponentConfig = {
  name: 'BookingDetailScreen',
  icon: '',
  category: S9ComponentConfigCategory.STACK9,
  version: S9ComponentConfigRelease.ALPHA,
  hasState: true,
  allowChildren: false,
  props: [],
};

const CustomEntityFormPage = S9CustomEntityFormPage.default;
const PageSection = S9PageSection.default;
const S9Button = ui.S9Button.default;
const S9Skeleton = ui.Skeleton.default;

export const BookingDetailScreen: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const {
    routeParams: { id = '' },
  } = useRouteAndQueryParams();

  const {
    data,
    isValidating: isLoading,
    mutate,
  } = ui.useScreenQueryById<BookingDetail>('getbookingbyid', +id);

  const formatDateTime = (date: string | Date | null | undefined) => {
    if (!date) return '-';
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  };

  const isCancelled = data?.status === 'Cancelled' || data?.cancellation_reason;

  return (
    <CustomEntityFormPage
      entityKey="booking"
      entityId={+id}
      title={`Booking #${id}`}
      cancelLink={AppRoutes.BookingList}
      screenQueryName="getbookingbyid"
    >
      {isLoading ? (
        <S9Skeleton active />
      ) : (
        data && (
          <>
            <PageSection
              size="sm"
              title=" "
              actions={
                <>
                  <S9Button
                    label="Edit"
                    icon="EditOutlined"
                    type="link"
                    onClick={() => setIsDrawerOpen(true)}
                  />
                </>
              }
            >
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Booking Reference',
                    children: data.booking_reference || '-',
                    span: 1,
                  },
                  {
                    label: 'Status',
                    children: data.status ? (
                      <Tag color={isCancelled ? 'red' : 'green'}>
                        {data.status}
                      </Tag>
                    ) : (
                      '-'
                    ),
                    span: 1,
                  },
                  {
                    label: 'Workflow',
                    children: data.workflow || '-',
                    span: 2,
                  },
                ]}
              />
            </PageSection>

            <PageSection size="sm" title="Event Information">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Event Name',
                    children: data.event?.event_name || '-',
                    span: 1,
                  },
                  {
                    label: 'Event Type',
                    children: data.event?.event_type || '-',
                    span: 1,
                  },
                  {
                    label: 'Booking Type',
                    children: data.event?.booking_type || '-',
                    span: 2,
                  },
                ]}
              />
            </PageSection>

            <PageSection size="sm" title="User Information">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'User Name',
                    children: data.user?.full_name || '-',
                    span: 1,
                  },
                  {
                    label: 'User Email',
                    children: data.user?.email || '-',
                    span: 1,
                  },
                  {
                    label: 'Position',
                    children: data.position || '-',
                    span: 1,
                  },
                  {
                    label: 'Department',
                    children: data.department?.name || '-',
                    span: 1,
                  },
                ]}
              />
            </PageSection>

            {data.event?.booking_type === 'Appointment' && data.slot_id && (
              <PageSection size="sm" title="Appointment Slot">
                <Descriptions
                  bordered
                  column={2}
                  items={[
                    {
                      label: 'Start Time',
                      children: data.slot?.start_dt
                        ? formatDateTime(data.slot.start_dt)
                        : '-',
                      span: 1,
                    },
                    {
                      label: 'End Time',
                      children: data.slot?.end_dt
                        ? formatDateTime(data.slot.end_dt)
                        : '-',
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}

            <PageSection size="sm" title="Booking Details">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Booking Date',
                    children: formatDateTime(data.booking_dt),
                    span: 2,
                  },
                ]}
              />
            </PageSection>

            <PageSection size="sm" title="Consent & Waiver">
              <Descriptions
                bordered
                column={2}
                items={[
                  {
                    label: 'Consent Given',
                    children: data.consent_given ? (
                      <Tag color="green">Yes</Tag>
                    ) : (
                      <Tag>No</Tag>
                    ),
                    span: 1,
                  },
                  {
                    label: 'Waiver Signed',
                    children: data.waiver_signed ? (
                      <Tag color="green">Yes</Tag>
                    ) : (
                      <Tag>No</Tag>
                    ),
                    span: 1,
                  },
                ]}
              />
            </PageSection>

            {isCancelled && (
              <PageSection size="sm" title="Cancellation">
                <Descriptions
                  bordered
                  column={1}
                  items={[
                    {
                      label: 'Cancellation Reason',
                      children: data.cancellation_reason || '-',
                      span: 1,
                    },
                  ]}
                />
              </PageSection>
            )}
          </>
        )
      )}
      <BookingDrawer
        open={isDrawerOpen}
        id={data?.id}
        onSaved={() => {
          setIsDrawerOpen(false);
          mutate();
        }}
        onDeleted={() => {
          setIsDrawerOpen(false);
          mutate();
        }}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      />
    </CustomEntityFormPage>
  );
};
